/* eslint-disable */
// DevImageUploader — New-DNA image zone for the New Development modal. Same
// contract as before (images / onChange / lastFocusedZoneRef): click + drag +
// document-paste (the paste listener lives in the orchestrator and routes via
// lastFocusedZoneRef). Per-thumbnail cluster: ★ set primary · ✦ remove-bg · × remove.
import React, { useState, useRef, useEffect } from "react";
import {
  compressImage, uploadImageToStorage, deleteStorageImages,
  isStorageUrl, removeBgForUrl
} from "./utils";
import { showToast as globalToast } from "./Toast";
import styles from "./DevImageUploader.module.css";

// Shared New-DNA image zone. Devs (the New Development modal) owns paste routing
// externally via lastFocusedZoneRef, so it passes no enablePaste. Originals (the
// New Item modal) has no Based-On slot, so it opts into the self-contained paste
// listener + onUploadingChange save-gate (additive — Devs behaviour unchanged).
export function DevImageUploader({ images, onChange, lastFocusedZoneRef, onUploadingChange, enablePaste = false }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const [removingBg, setRemovingBg] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
  const MAX_SIZE = 10 * 1024 * 1024;
  const imgsRef = useRef(images);
  imgsRef.current = images;

  // Click/focus marks this as the 'main' zone so a subsequent paste lands here.
  const markMain = () => { if (lastFocusedZoneRef) lastFocusedZoneRef.current = "main"; };

  const processFiles = async (fileList) => {
    const current = imgsRef.current;
    const files = Array.from(fileList).filter((f) => ALLOWED_TYPES.includes(f.type) && f.size <= MAX_SIZE);
    if (files.length === 0) return;
    setUploading(true);
    onUploadingChange?.(true);
    const urls = [];
    for (const file of files) {
      const compressed = await compressImage(file);
      const url = await uploadImageToStorage(compressed);
      urls.push(url || compressed);
    }
    if (urls.length) onChange([...current, ...urls]);
    setUploading(false);
    onUploadingChange?.(false);
  };

  // Opt-in document-level paste (Originals). Devs handles paste in its
  // orchestrator (usePasteImages) and leaves enablePaste off.
  useEffect(() => {
    if (!enablePaste) return undefined;
    const onPaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imgItems = items.filter((it) => it.type.startsWith("image/"));
      if (!imgItems.length) return;
      e.preventDefault();
      const files = imgItems.map((it) => it.getAsFile()).filter(Boolean);
      if (files.length) processFiles(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePaste, images]);

  const handleRemoveBg = async (idx) => {
    setRemovingBg(idx);
    try {
      const newUrl = await removeBgForUrl(images[idx], { deleteOriginal: true });
      onChange(images.map((img, i) => (i === idx ? newUrl : img)));
    } catch (e) {
      globalToast("Background removal failed: " + e.message, "error");
    }
    setRemovingBg(null);
  };

  // ★ — make image i the primary by moving it to the front (primary = images[0]).
  const setPrimary = (i) => { if (i > 0) onChange([images[i], ...images.filter((_, j) => j !== i)]); };
  const removeImg = (i) => {
    if (isStorageUrl(images[i])) deleteStorageImages([images[i]]);
    onChange(images.filter((_, j) => j !== i));
  };

  const dragProps = {
    onDragOver: (e) => { e.preventDefault(); setDragOver(true); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); },
    onDrop: (e) => { markMain(); e.preventDefault(); setDragOver(false); processFiles(e.dataTransfer.files); },
  };

  return (
    <div>
      {images.length === 0 ? (
        <div
          className={`${styles.zone} ${dragOver ? styles.over : ""} ${uploading ? styles.busy : ""}`}
          onClick={() => { markMain(); if (!uploading) ref.current.click(); }}
          onFocusCapture={markMain}
          {...dragProps}
        >
          <svg className={styles.zoneIcon} viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15l-5-5L5 21" /><path d="M12 3v12" /><path d="M8 7l4-4 4 4" />
          </svg>
          <div className={styles.zoneTitle}>{dragOver ? "Release to upload" : uploading ? "Uploading…" : "Drop images here"}</div>
          <div className={styles.zoneHint}>Click to browse · Ctrl+V · Drag</div>
          <div className={styles.zoneChip}>JPG · PNG · WEBP · max 10MB</div>
        </div>
      ) : (
        <div className={`${styles.grid} ${dragOver ? styles.over : ""}`} onClick={markMain} onFocusCapture={markMain} {...dragProps}>
          {images.map((src, i) => (
            <div key={i} className={`${styles.cell} ${i === 0 ? styles.primary : ""}`}>
              <img src={src} alt="" loading="lazy" />
              {i === 0 && <span className={styles.primaryBadge}>Primary</span>}
              {removingBg === i && <div className={styles.bgOverlay}>Removing…</div>}
              {removingBg === null && (
                <div className={styles.actions}>
                  <button type="button" className={`${styles.actBtn} ${i === 0 ? styles.starOn : ""}`} title={i === 0 ? "Primary image" : "Set as primary"} onClick={(e) => { e.stopPropagation(); setPrimary(i); }}>
                    <svg viewBox="0 0 24 24" width="12" height="12" fill={i === 0 ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15 9 22 9 16.5 13.5 18.5 21 12 16.5 5.5 21 7.5 13.5 2 9 9 9 12 2" />
                    </svg>
                  </button>
                  <button type="button" className={styles.actBtn} title="Remove background" onClick={(e) => { e.stopPropagation(); handleRemoveBg(i); }}>
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 3v4" /><path d="M3 5h4" /><path d="M19 17v4" /><path d="M17 19h4" /><path d="M10 4l2.5 6.5L19 13l-6.5 2.5L10 22l-2.5-6.5L1 13l6.5-2.5z" />
                    </svg>
                  </button>
                  <button type="button" className={`${styles.actBtn} ${styles.danger}`} title="Remove" onClick={(e) => { e.stopPropagation(); removeImg(i); }}>
                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
          <div className={styles.addCell} onClick={() => { if (!uploading) ref.current.click(); }}>
            <span className={styles.addPlus}>+</span>
            <span className={styles.addLabel}>{uploading ? "Uploading…" : "Add more"}</span>
          </div>
        </div>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple style={{ display: "none" }} onChange={(e) => processFiles(e.target.files)} />
    </div>
  );
}
